// The rank's views, shown in the Lobby's « Rang » and « Social » tabs (components/lobby/): the player's
// rank and record, the leaderboard, the players met recently. A tap on a player opens their card.
import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { RankEmblem } from "./RankEmblem";
import { RankBar, Stat, fmt, labelOf, nextLabel, tierName } from "./rankParts";
import { PresenceDot } from "../players/PlayerCardDialog";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import { CALIBRATION_GAMES, TIERS, rankInfo } from "../../lib/ranks";
import { t } from "../../lib/i18n";

// The player's rank (GET /api/rank), read when the Lobby opens.
export function useMyRank() {
  const [rank, setRank] = useState(null);
  const refresh = useCallback(() => {
    api.get("/rank").then(({ data }) => setRank(data.rank)).catch(() => {});
  }, []);
  useEffect(refresh, [refresh]);
  return [rank, refresh];
}

/** The player's rank: emblem, points to the next step, the last game's change, 9 numbers, the ladder. */
export function MyRank({ rank }) {
  const { user } = useAuth();
  const info = rankInfo(rank);
  const r = rank || {};
  const peak = rankInfo({ rp: r.peak_rp ?? 0, games: CALIBRATION_GAMES });
  const rate = r.games ? `${Math.round((100 * r.wins) / r.games)} %` : "—";
  const last = r.last;

  return (
    <div>
      {/* The emblem beside the rank, its points and the bar (a row: compact, owner's request). */}
      <div className="flex items-center gap-3">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 300, damping: 18 }}>
          <RankEmblem info={info} size={68} title={labelOf(info)} />
        </motion.div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate font-display text-2xl italic leading-tight text-amber-200" data-testid="rank-label">{labelOf(info)}</span>
            {/* The points show from the first game, calibrating or not (owner's request). */}
            <span className="shrink-0 text-xs tabular-nums text-slate-300" data-testid="rank-points">{t("rank.pr", { n: fmt(info.rp) })}</span>
          </div>
          {info.calibrating ? (
            <div className="mt-1.5 flex items-center gap-2">
              <RankBar info={{ progress: info.games / CALIBRATION_GAMES }} className="min-w-0 flex-1" />
              <span className="shrink-0 text-[11px] leading-none tabular-nums text-slate-400" data-testid="rank-calibration-count">
                {t("rank.calibrationCount", { n: info.games, total: CALIBRATION_GAMES })}
              </span>
            </div>
          ) : (
            <RankBar info={info} className="mt-1.5" />
          )}
          <p className="mt-1 text-[11px] leading-snug text-slate-400">
            {info.calibrating ? t("rank.calibrationNote", { n: CALIBRATION_GAMES }) : nextLabel(info)}
            {last && (
              <>
                {" · "}{t("rank.last")}{" "}
                <span className={last.delta >= 0 ? "text-emerald-300" : "text-rose-300"}>
                  {t(last.delta >= 0 ? "rank.gain" : "rank.drop", { n: Math.abs(last.delta) })}
                </span>
              </>
            )}
          </p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-1.5" data-testid="rank-stats">
        <Stat label={t("rank.stats.games")} value={r.games ?? 0} />
        <Stat label={t("rank.stats.wins")} value={r.wins ?? 0} accent="text-emerald-300" />
        <Stat label={t("rank.stats.losses")} value={r.losses ?? 0} accent="text-rose-300" />
        <Stat label={t("rank.stats.winRate")} value={rate} />
        <Stat label={t("rank.stats.streak")} value={r.streak ?? 0} accent={r.streak >= 2 ? "text-orange-300" : undefined} />
        <Stat label={t("rank.stats.bestStreak")} value={r.best_streak ?? 0} />
        <Stat label={t("rank.stats.roundsWon")} value={r.rounds_won ?? 0} />
        <Stat label={t("rank.stats.abandons")} value={r.abandons ?? 0} />
        <div className="flex flex-col items-center justify-center rounded-xl border border-white/5 bg-slate-950/50 px-1 py-1.5">
          <span className="flex items-center gap-1">
            <RankEmblem info={info.calibrating ? info : peak} size={22} />
            <span className="truncate text-xs text-amber-200">{labelOf(info.calibrating ? info : peak)}</span>
          </span>
          <div className="mt-0.5 text-[10px] leading-tight text-slate-400">{t("rank.stats.peak")}</div>
        </div>
      </div>

      <p className="mt-3 text-[11px] uppercase tracking-wider text-slate-400">{t("rank.ladder")}</p>
      <div className="mt-1.5 grid grid-cols-6 gap-1" data-testid="rank-ladder">
        {TIERS.map((tier, i) => {
          const tInfo = { ...rankInfo({ rp: tier.floor, games: CALIBRATION_GAMES }), division: null };
          const reached = !info.calibrating && i <= info.tierIndex;
          const here = !info.calibrating && i === info.tierIndex;
          return (
            <div key={tier.id} className={`flex flex-col items-center rounded-lg py-1 ${here ? "bg-amber-400/10 ring-1 ring-amber-400/40" : ""}`}>
              <RankEmblem info={tInfo} size={24} className={reached ? "" : "opacity-35 grayscale"} />
              <span className={`mt-0.5 text-[9px] leading-tight ${here ? "text-amber-200" : "text-slate-500"}`}>{tierName(tInfo)}</span>
              <span className="text-[8px] tabular-nums text-slate-600" dir="ltr">{fmt(tier.floor)}</span>
            </div>
          );
        })}
      </div>

      <details className="group mt-2.5 rounded-xl border border-white/5 bg-slate-950/40 px-3 py-1.5 text-xs text-slate-300">
        <summary className="cursor-pointer select-none text-slate-200 marker:text-amber-400">{t("rank.how.title")}</summary>
        <ul className="mt-2 list-disc space-y-1 ps-4 text-slate-400">
          <li>{t("rank.how.place")}</li>
          <li>{t("rank.how.humans")}</li>
          <li>{t("rank.how.winners")}</li>
          <li>{t("rank.how.strength")}</li>
          <li>{t("rank.how.kept")}</li>
          <li>{t("rank.how.quit")}</li>
        </ul>
      </details>

      {user?.is_guest && t("rank.guestNote") && (
        <p className="mt-3 text-xs text-amber-200/70" data-testid="rank-guest-note">{t("rank.guestNote")}</p>
      )}
    </div>
  );
}

/** One player in a list (leaderboard, recent players): a tap opens their card; `action` sits at the end. */
export function PlayerRow({ row, place, mine, status, onOpen, children, action }) {
  const info = rankInfo(row.rank || { rp: row.rp, games: row.games });
  return (
    <li className={`flex items-center gap-1.5 rounded-xl pe-1.5 transition-colors ${mine ? "bg-amber-400/10 ring-1 ring-amber-400/40" : "bg-slate-950/40 hover:bg-white/[0.06]"}`}>
      <button
        type="button"
        onClick={() => onOpen(row.user_id)}
        aria-label={t("players.open", { name: row.name })}
        data-testid={`player-row-${row.user_id}`}
        className="flex min-w-0 flex-1 items-center gap-2.5 px-2 py-1 text-start"
      >
        {place != null && (
          <span className={`w-6 shrink-0 text-center text-xs tabular-nums ${place <= 3 ? "text-amber-300" : "text-slate-500"}`}>{place}</span>
        )}
        <span className="relative shrink-0">
          <Avatar className="h-7 w-7">
            <AvatarImage src={row.picture} />
            <AvatarFallback className="bg-emerald-900 text-[11px] text-amber-300">{row.name?.[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          {status && <PresenceDot status={status} className="absolute -bottom-0.5 -end-0.5 h-3 w-3" />}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-white" dir="auto">{mine ? t("rank.board.you") : row.name}</span>
        <RankEmblem info={info} size={22} title={labelOf(info)} />
        {children}
      </button>
      {action}
    </li>
  );
}

const Spinner = () => <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-amber-300" /></div>;

/** The 50 best ranked players, and the player's place. */
export function Leaderboard({ onOpen }) {
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/rank/leaderboard").then(({ data }) => setBoard(data)).catch(() => setError(true));
  }, []);

  if (error) return <p className="px-5 py-8 text-center text-sm text-slate-400">{t("rank.board.error")}</p>;
  if (!board) return <Spinner />;

  return (
    <div>
      {board.rows.length === 0 ? (
        <p className="px-2 py-8 text-center text-sm text-slate-400">{t("rank.board.empty")}</p>
      ) : (
        <ol className="space-y-1" data-testid="rank-board">
          {board.rows.map((row, i) => (
            <PlayerRow key={row.user_id} row={row} place={i + 1} mine={row.user_id === user?.user_id} onOpen={onOpen}>
              <span className="w-14 shrink-0 text-end text-xs tabular-nums text-slate-300">{fmt(row.rp)}</span>
            </PlayerRow>
          ))}
        </ol>
      )}
      <p className="mt-3 text-center text-xs text-slate-400" data-testid="rank-board-me">
        {board.position ? t("rank.board.position", { n: board.position }) : t("rank.board.unranked")}
      </p>
    </div>
  );
}

const STATUS_TEXT = { online: "text-emerald-300", in_game: "text-amber-300", offline: "text-slate-500" };

/**
 * The players this account last shared a table with (GET /api/players/recent), whether they are online
 * now. `action(player)`: what sits at the end of a row (the Social tab's « Inviter »).
 */
export function Recent({ onOpen, action }) {
  const [players, setPlayers] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/players/recent").then(({ data }) => setPlayers(data.players)).catch(() => setError(true));
  }, []);

  if (error) return <p className="px-5 py-8 text-center text-sm text-slate-400">{t("players.error")}</p>;
  if (!players) return <Spinner />;
  if (!players.length) return <p className="px-6 py-6 text-center text-sm text-slate-400" data-testid="recent-empty">{t("players.recentEmpty")}</p>;

  return (
    <ol className="space-y-1" data-testid="recent-players">
      {players.map((p) => (
        <PlayerRow key={p.user_id} row={p} status={p.status} onOpen={onOpen} action={action?.(p)}>
          {!action && (
            <span className={`w-16 shrink-0 truncate text-end text-[10px] ${STATUS_TEXT[p.status]}`}>{t(`players.status.${p.status}`)}</span>
          )}
        </PlayerRow>
      ))}
    </ol>
  );
}
